import { useState, FC, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Pencil } from 'lucide-react';
import { Service, ServiceCategory as Category, Unit } from '@/shared/model';
import type { ServiceManualField } from '@/entities/service';
import { parseSafeNumber } from '@/shared/lib/number';

interface ServicesTableProps {
    services: Service[];
    categories: Category[];
    units: Unit[];
    onUpdate: (id: string, field: string, value: string | number | boolean) => void;
    onDelete: (id: string) => void;
}

/**
 * Quiet inline "your value, not the base catalog's" affordance (Phase 37, CATALOG-04).
 * Renders strictly from the recorded `manualFields` claim -- never from a runtime comparison
 * of the displayed value against the bundle default -- so a value the user reverts back to the
 * default (which clears the claim, see 37-01's writers) correctly reads as inherited again.
 * A 6px dot in the blue family already used throughout this file (text-blue-600, border-blue-400,
 * bg-blue-50) is the quietest affordance that still reads at ~185-row density; it deliberately
 * does not reuse the Pencil icon already occupying the hover-affordance role below.
 */
const ManualMarker: FC<{ service: Service; field: ServiceManualField }> = ({ service, field }) => {
    const { t } = useTranslation();
    if (!service.manualFields?.includes(field)) return null;
    const label = t('catalog.table.manual_marker', 'Ваше значение — не из базового каталога');
    return (
        <span
            data-testid={`manual-marker-${field}`}
            role="img"
            title={label}
            aria-label={label}
            className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 shrink-0 align-middle ml-1"
        />
    );
};

export const ServicesTable: FC<ServicesTableProps> = ({
    services, 
    categories, 
    units, 
    onUpdate, 
    onDelete 
}) => {
    const { t } = useTranslation();
    const [exitingIds, setExitingIds] = useState<string[]>([]);

    const handleRemoveWithAnimation = (id: string) => {
        setExitingIds(prev => [...prev, id]);
        setTimeout(() => {
            onDelete(id);
            setExitingIds(prev => prev.filter(eid => eid !== id));
        }, 300);
    };

    return (
        <table className="w-full text-sm text-left">
            <thead className="text-[10px] text-slate-500 dark:text-slate-400 uppercase bg-blue-50/60 dark:bg-slate-800/80 backdrop-blur-sm border-b border-blue-100 dark:border-slate-800 sticky top-0 z-20 shadow-sm">
                <tr>
                    <th className="px-6 py-4 font-semibold tracking-wider">{t('catalog.table.name', 'Название')}</th>
                    <th className="hidden md:table-cell px-4 py-4 font-semibold tracking-wider">{t('catalog.table.category', 'Категория')}</th>
                    <th className="hidden md:table-cell px-4 py-4 font-semibold tracking-wider w-24">{t('catalog.table.unit_short', 'Ед.')}</th>
                    <th className="hidden md:table-cell px-4 py-4 font-semibold tracking-wider text-right w-24 md:w-32">{t('catalog.table.price', 'Цена')}</th>
                    <th className="hidden md:table-cell w-12 md:w-16"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {services.map(s => {
                    const isExiting = exitingIds.includes(s.id);
                    const unitType = s.unitType || s.unit;
                    
                    return (
                        <tr 
                            key={s.id} 
                            className={`
                                transition-colors duration-200 ease-in-out group
                                ${isExiting ? 'bg-red-50 dark:bg-red-900/10 pointer-events-none' : 'hover:bg-blue-50/30 dark:hover:bg-blue-900/10'}
                            `}
                        >
                            <td className={`px-6 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-3'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-40 opacity-100'}`}>
                                    <div className="flex items-start gap-1">
                                        <textarea
                                            value={s.name}
                                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onUpdate(s.id, 'name', e.target.value)}
                                            className="w-full bg-transparent border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 font-bold text-slate-700 dark:text-slate-200 resize-none h-auto overflow-hidden min-h-[1.5em]"
                                            rows={1}
                                            onInput={(e: React.FormEvent<HTMLTextAreaElement>) => { const target = e.target as HTMLTextAreaElement; target.style.height = 'auto'; target.style.height = target.scrollHeight + 'px'; }}
                                        />
                                        <ManualMarker service={s} field="name" />
                                        <Pencil
                                            size={11}
                                            className="opacity-0 group-hover:opacity-30 transition-opacity duration-150 text-slate-400 dark:text-slate-600 shrink-0 mt-1 ml-1"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <input
                                        value={s.description || ''}
                                        onChange={(e) => onUpdate(s.id, 'description', e.target.value)}
                                        placeholder={t('catalog.add_description', 'Добавить описание...')}
                                        className="w-full bg-transparent border-b border-transparent hover:border-slate-200 dark:hover:border-slate-700 focus:border-blue-300 dark:focus:border-blue-700 focus:outline-none transition-colors duration-150 text-[10px] text-slate-400 dark:text-slate-500 font-medium placeholder:text-slate-300 dark:placeholder:text-slate-600 pb-1"
                                    />
                                    <ManualMarker service={s} field="description" />

                                    <div className="md:hidden flex flex-col gap-2 mt-2">
                                        <div className="flex gap-2">
                                            <div className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-md px-2 py-1.5 shadow-sm border border-slate-100 dark:border-slate-700">
                                                <select 
                                                    value={s.categoryId || ''}
                                                    onChange={(e) => onUpdate(s.id, 'categoryId', e.target.value)}
                                                    className="w-full bg-transparent text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase outline-none appearance-none"
                                                >
                                                    <option value="" className="dark:bg-slate-800">{t('catalog.no_category', 'Без категории')}</option>
                                                    {categories.map(c => <option key={c.id} value={c.id} className="dark:bg-slate-800">{c.nameKey ? t(c.nameKey, c.name) : c.name}</option>)}
                                                </select>
                                                <ManualMarker service={s} field="categoryId" />
                                            </div>
                                            <div className="w-1/3 bg-slate-50 dark:bg-slate-800 rounded-md px-2 py-1.5 shadow-sm border border-slate-100 dark:border-slate-700">
                                                <select
                                                    value={unitType}
                                                    onChange={(e) => onUpdate(s.id, 'unitType', e.target.value)}
                                                    className="w-full bg-transparent text-[11px] font-bold text-slate-600 dark:text-slate-400 outline-none appearance-none"
                                                >
                                                    {units.map(u => <option key={u.id} value={u.id} className="dark:bg-slate-800">{u.nameKey ? t(u.nameKey, u.name) : u.name}</option>)}
                                                </select>
                                                <ManualMarker service={s} field="unitType" />
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center justify-between h-9 bg-slate-50/50 dark:bg-slate-800/50 rounded-md px-1">
                                                <div className="flex items-center bg-white dark:bg-slate-800 rounded-md px-3 h-8 shadow-sm border border-slate-100 dark:border-slate-700 w-32">
                                                <input
                                                    type="number"
                                                    value={s.priceLabor}
                                                    onChange={(e) => onUpdate(s.id, 'priceLabor', parseSafeNumber(e.target.value))}
                                                    className="w-full text-right bg-transparent outline-none font-bold font-mono tabular-nums text-slate-800 dark:text-slate-100 text-sm p-0 m-0 leading-none h-full"
                                                />
                                                <ManualMarker service={s} field="priceLabor" />
                                                </div>
                                                <button
                                                onClick={() => handleRemoveWithAnimation(s.id)}
                                                aria-label={t('catalog.delete_service', 'Удалить услугу')}
                                                className="w-8 h-8 flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            >
                                                <Trash2 size={18} aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-3'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <select 
                                        value={s.categoryId || ''}
                                        onChange={(e) => onUpdate(s.id, 'categoryId', e.target.value)}
                                        className="w-full bg-transparent text-xs font-bold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 focus:text-blue-600 dark:focus:text-blue-400 border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 py-1 appearance-none"
                                    >
                                        <option value="" className="dark:bg-slate-900">{t('catalog.no_category', 'Без категории')}</option>
                                        {categories.map(c => <option key={c.id} value={c.id} className="dark:bg-slate-900">{c.nameKey ? t(c.nameKey, c.name) : c.name}</option>)}
                                    </select>
                                    <ManualMarker service={s} field="categoryId" />
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-3'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <select
                                        value={unitType}
                                        onChange={(e) => onUpdate(s.id, 'unitType', e.target.value)}
                                        className="bg-transparent text-xs font-bold text-slate-500 dark:text-slate-400 w-full cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 focus:text-blue-600 dark:focus:text-blue-400 border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 py-1 appearance-none"
                                    >
                                        {units.map(u => <option key={u.id} value={u.id} className="dark:bg-slate-900">{u.nameKey ? t(u.nameKey, u.name) : u.name}</option>)}
                                    </select>
                                    <ManualMarker service={s} field="unitType" />
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 text-right align-middle transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-3'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <input
                                        type="number"
                                        value={s.priceLabor}
                                        onChange={(e) => onUpdate(s.id, 'priceLabor', parseSafeNumber(e.target.value))}
                                        className="w-full text-right bg-transparent border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 font-bold font-mono tabular-nums text-slate-800 dark:text-slate-100 py-1"
                                    />
                                    <ManualMarker service={s} field="priceLabor" />
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 text-right align-middle transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-3'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <button
                                        onClick={() => handleRemoveWithAnimation(s.id)}
                                        aria-label={t('catalog.delete_service', 'Удалить услугу')}
                                        className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 size={16} aria-hidden="true" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
